import React, {useState} from "react";
import {track} from "libs/analytics";

import {
  Textarea,
  Checkbox,
  Input,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "biggerui";
import {
  useFeatureFlags,
  useCreateFeatureMutation,
  useDeleteFeatureMutation,
  useUpdateFeatureMutation,
  TARGET_OPTIONS,
  EMPTY_FEATURE,
} from "hooks/featureFlags";
import {isRegisteredFeature} from "data/features";
import {mayChangeFlagTarget} from "data/permissions";
import {useAuth} from "hooks/useAuth";
import {FeatureCard} from "../components";
import RoleFeatures from "./RoleFeatures";

import "./index.css";

function IconSave() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M11.3333 14V9.33333C11.3333 9.15652 11.2631 8.98695 11.1381 8.86193C11.013 8.7369 10.8435 8.66667 10.6667 8.66667H5.33333C5.15652 8.66667 4.98695 8.7369 4.86193 8.86193C4.7369 8.98695 4.66667 9.15652 4.66667 9.33333V14M4.66667 2V4.66667C4.66667 4.84348 4.7369 5.01305 4.86193 5.13807C4.98695 5.2631 5.15652 5.33333 5.33333 5.33333H10M10.1333 2C10.485 2.00501 10.8205 2.14878 11.0667 2.4L13.6 4.93333C13.8512 5.17951 13.995 5.51497 14 5.86667V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8594 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V3.33333C2 2.97971 2.14048 2.64057 2.39052 2.39052C2.64057 2.14048 2.97971 2 3.33333 2H10.1333Z"
        stroke="#D4D4D4"
        strokeWidth="1.33"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const FlagsPage = () => {
  const {role: callerRole} = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [featureToDelete, setFeatureToDelete] = useState(null);

  const {featureFlags, loading, error, fetchFeatureFlags, setError} = useFeatureFlags();

  const [currentFeature, setCurrentFeature] = useState(EMPTY_FEATURE);
  // Flags that no code reads: a flag has an effect only as a registered
  // feature (data/features.ts), which the table above shows.
  const otherFlags = featureFlags.filter((flag) => !isRegisteredFeature(flag.key));

  const createFeatureMutation = useCreateFeatureMutation(
    () => {
      setError(null);
      track("neurobaker.admin.flag_changed", {action: "create", key: currentFeature.key});
      setCurrentFeature(EMPTY_FEATURE);
      setCreateDialogOpen(false);
      fetchFeatureFlags();
    },
    () => {
      setError("Failed to create feature flag");
    }
  );

  const updateFeatureMutation = useUpdateFeatureMutation(
    () => {
      setError(null);
      track("neurobaker.admin.flag_changed", {action: "update"});
      fetchFeatureFlags();
    },
    () => {
      setError("Failed to update feature flag");
    }
  );

  const deleteFeatureMutation = useDeleteFeatureMutation(
    () => {
      setError(null);
      track("neurobaker.admin.flag_changed", {action: "delete", key: featureToDelete?.key});
      setFeatureToDelete(null);
      setDeleteDialogOpen(false);
      fetchFeatureFlags();
    },
    () => {
      setError("Failed to delete feature flag");
    }
  );

  const handleOpenCreate = () => {
    setCurrentFeature(EMPTY_FEATURE);
    setCreateDialogOpen(true);
  };

  const handleFeatureFieldChange = (e) => {
    const {name, value} = e.target;
    setCurrentFeature((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTargetToggleInCreate = (key, checked) => {
    setCurrentFeature((prev) => ({
      ...prev,
      targets: {
        ...prev.targets,
        [key]: checked,
      },
    }));
  };

  const handleSubmitCreate = () => {
    if (!currentFeature.key || !currentFeature.name) return;
    createFeatureMutation.mutate(currentFeature);
  };

  const onDelete = (feature) => {
    setFeatureToDelete(feature);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!featureToDelete) return;
    deleteFeatureMutation.mutate(featureToDelete.key);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setFeatureToDelete(null);
  };

  if (loading) {
    return (
      <div className="admin-flags-feature-flags-container">
        <div className="admin-flags-page-header">
          <h1 className="admin-flags-page-title">Feature Flags</h1>
        </div>
        <div className="admin-flags-loading">Loading feature flags...</div>
      </div>
    );
  }

  return (
    <div className="admin-flags-feature-flags-container">
      <div className="admin-flags-page-header">
        <div>
          <h1 className="admin-flags-page-title">Feature Flags</h1>
          <p className="admin-flags-page-subtitle">
            Choose which roles see which editor features.
          </p>
        </div>
        <Button leftIcon={<IconSave />} onClick={handleOpenCreate}>
          Create feature flag
        </Button>
      </div>

      {error && <div className="admin-flags-error-message">{error}</div>}

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)}>
        <DialogHeader>
          <DialogTitle>
            <span className="admin-flags-dialog-title-main">Create feature flag</span>
          </DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="admin-flags-dialog-form">
            <div className="admin-flags-dialog-row">
              <div className="admin-flags-dialog-field">
                <Input
                  label="Key"
                  placeholder="unique_feature_key"
                  name="key"
                  value={currentFeature.key}
                  onChange={handleFeatureFieldChange}
                />
              </div>
              <div className="admin-flags-dialog-field">
                <Input
                  label="Name"
                  placeholder="Short human-readable name"
                  name="name"
                  value={currentFeature.name}
                  onChange={handleFeatureFieldChange}
                />
              </div>
            </div>

            <div className="admin-flags-dialog-field">
              <Textarea
                label="Description"
                name="description"
                value={currentFeature.description}
                onChange={handleFeatureFieldChange}
                placeholder="Explain what this flag controls and how it should be used."
              />
            </div>

            <div className="admin-flags-dialog-row">
              <div className="admin-flags-dialog-field">
                <div className="admin-flags-field-label">Targets</div>
                <div className="admin-flags-targets-grid">
                  {TARGET_OPTIONS.map((target) => (
                    <div key={target.key} className="admin-flags-target-option">
                      <Checkbox
                        checked={!!currentFeature.targets[target.key]}
                        disabled={!mayChangeFlagTarget(callerRole, target.key)}
                        onChange={(checked) => handleTargetToggleInCreate(target.key, checked)}
                        label={target.label}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setCreateDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmitCreate}
            disabled={!currentFeature.key || !currentFeature.name}
          >
            Create flag
          </Button>
        </DialogFooter>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogHeader>
          <DialogTitle>
            <span className="admin-flags-dialog-title-main">Delete feature flag</span>
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="admin-flags-dialog-form">
            <p className="admin-flags-confirm-text">
              Are you sure you want to delete this feature flag?
            </p>
            {featureToDelete && (
              <div className="admin-flags-confirm-info">
                <div>
                  <span className="admin-flags-meta-label">Name: </span>
                  <span className="admin-flags-meta-value">{featureToDelete.name || "—"}</span>
                </div>
                <div>
                  <span className="admin-flags-meta-label">Key: </span>
                  <span className="admin-flags-meta-value">{featureToDelete.key}</span>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogFooter>
      </Dialog>

      <RoleFeatures
        docs={featureFlags}
        onChanged={() => {
          setError(null);
          fetchFeatureFlags();
        }}
        onError={setError}
      />

      <div>
        <h2 className="admin-flags-section-title">Other flags</h2>
        <p className="admin-flags-section-text">
          No code reads these flags. To make a flag work, register it in src/data/features.ts.
        </p>
      </div>
      <div className="admin-flags-feature-flags-list">
        {otherFlags.length === 0 ? (
          <div className="admin-flags-empty-state">
            <p className="admin-flags-empty-title">No other flags</p>
          </div>
        ) : (
          otherFlags.map((feature) => (
            <FeatureCard
              key={feature.key}
              feature={feature}
              onDelete={onDelete}
              onUpdate={updateFeatureMutation}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default FlagsPage;
